import { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Flex,
  Grid,
  Modal,
  Typography,
  Input,
  message,
  Row,
  Col,
} from "antd";
import { Icon } from "@iconify/react";
import { ExclamationCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import { getFileExtension } from "../../utils/getFileExtension";
import pallete from "../../utils/pallete";
import { trainingVerificationService } from "../../services/training-verification.service";
import { MODAL_MESSAGES } from "../../utils/trainingCatalog.constants";
import StatusTag from "../../components/StatusTag";
import { useGetDetailApi } from "../../hooks/useApi";
import { format } from "date-fns";
import { fileService } from "../../services/file.service";
import DocumentPreviewModal from "../../components/DocumentPreviewModal";

export default function TrainingVerificationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const breakpoint = Grid.useBreakpoint();
  const queryClient = useQueryClient();
  const [messageApi, contextHolder] = message.useMessage();

  const [previewFileData, setPreviewFileData] = useState(null);
  const [confirmVerify, setConfirmVerify] = useState(false);
  const [confirmReject, setConfirmReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [confirmRevise, setConfirmRevise] = useState(false);
  const [reviseReason, setReviseReason] = useState("");

  const extractUUID = (filePath) => {
    if (!filePath) return null;
    const match = filePath.match(
      /[0-9a-fA-F]{8}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{12}/,
    );
    return match ? match[0] : null;
  };

  const handlePreview = async (filePath) => {
    const uuid = extractUUID(filePath);
    if (!uuid) {
      messageApi.error("UUID file tidak ditemukan");
      return;
    }

    try {
      messageApi.loading({ content: "Memuat dokumen...", key: "preview" });
      const blob = await fileService.getFile(uuid);
      const url = URL.createObjectURL(blob);
      setPreviewFileData({
        url,
        isPdf: getFileExtension(filePath) === "pdf",
      });
      messageApi.success({
        content: "Dokumen berhasil dimuat",
        key: "preview",
        duration: 2,
      });
    } catch (error) {
      console.error("Preview error:", error);
      messageApi.error({
        content: "Gagal memuat dokumen",
        key: "preview",
        duration: 2,
      });
    }
  };

  const handleDownloadFile = async (filePath, fileName) => {
    const uuid = extractUUID(filePath);
    if (!uuid) {
      messageApi.error("UUID file tidak ditemukan");
      return;
    }

    try {
      messageApi.loading({ content: "Mengunduh dokumen...", key: "download" });
      const blob = await fileService.getFile(uuid);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      messageApi.success({
        content: "Dokumen berhasil diunduh",
        key: "download",
        duration: 2,
      });
    } catch (error) {
      console.error("Download error:", error);
      messageApi.error({
        content: "Gagal mengunduh dokumen",
        key: "download",
        duration: 2,
      });
    }
  };

  /** 🔹 Fetch data detail */
  const { data: detailResponse } = useGetDetailApi({
    id,
    serviceFn: trainingVerificationService.fetchById,
    queryKey: ["training-verification", "detail"],
  });

  const detailData = detailResponse;
  const showActions =
    detailData?.status == "Ditolak" || detailData?.status == "Diterima";

  const labelStyle = {
    width: breakpoint.lg ? 260 : "100%",
    minWidth: 260,
  };

  const contentStyle = {
    width: breakpoint.lg ? "calc(100% - 260px)" : "100%",
  };

  const descriptionTitle = (title) => (
    <Typography.Title
      level={3}
      style={{
        marginBottom: 0,
        fontWeight: "bold",
        fontSize: 18,
      }}
    >
      {title}
    </Typography.Title>
  );

  const { mutate: changeStatus, isPending: isChangingStatus } = useMutation({
    mutationFn: ({ status, reason }) =>
      trainingVerificationService.changeStatus(id, status, reason),
    onSuccess: (_, { status }) => {
      messageApi.success(
        status === "accepted"
          ? "Berhasil diverifikasi"
          : status === "rejected"
            ? "Berhasil ditolak"
            : "Berhasil dikembalikan untuk revisi",
      );

      setConfirmVerify(false);
      setConfirmReject(false);
      setConfirmRevise(false);
      setRejectReason("");
      setReviseReason("");

      queryClient.invalidateQueries(["training-verification"]);
      navigate(-1); // ⬅️ lebih aman daripada backRoute
    },
    onError: () => {
      messageApi.error("Gagal mengubah status");
    },
  });

  const handleVerify = () => {
    changeStatus({ status: "accepted" });
  };

  const handleReject = () => {
    if (!rejectReason.trim()) {
      messageApi.warning("Alasan penolakan wajib diisi");
      return;
    }
    changeStatus({ status: "rejected", reason: rejectReason });
  };

  const handleRevise = () => {
    if (!reviseReason.trim()) {
      messageApi.warning("Alasan revisi wajib diisi");
      return;
    }
    changeStatus({ status: "revised", reason: reviseReason });
  };

  /** 🔹 Status Section */
  const statusItems = useMemo(() => {
    if (!detailData) return [];

    const items = [
      {
        label: "Status",
        children: <StatusTag status={detailData.status} />,
        span: 2,
      },
    ];

    if (detailData.status === "Revisi" || detailData.status === "Ditolak") {
      items.push({
        label: "Alasan",
        children: detailData.reason,
        span: 2,
      });
    }

    return items;
  }, [detailData]);

  /** 🔹 Data diri */
  const profileDataItems = useMemo(
    () =>
      !detailData
        ? []
        : [
            { label: "NIK", children: detailData.nik || "-" },
            { label: "Nama Depan", children: detailData.firstName || "-" },
            { label: "Nama Belakang", children: detailData.lastName || "-" },
            {
              label: "Jenis Kelamin",
              children: detailData.gender === "L" ? "Laki-Laki" : "Perempuan",
            },
            { label: "Tempat Lahir", children: detailData.birthPlace || "-" },
            {
              label: "Tanggal Lahir",
              children: detailData.birthDate
                ? format(new Date(detailData.birthDate), "dd/MM/yyyy")
                : "-",
            },
            { label: "Email", children: detailData.email || "-" },
            { label: "Telepon", children: detailData.phone || "-" },
          ],
    [detailData],
  );

  const areaOriginItems = useMemo(
    () =>
      !detailData
        ? []
        : [
            { label: "Provinsi", children: detailData.province || "-" },
            { label: "Kabupaten/Kota", children: detailData.city || "-" },
            { label: "Kecamatan", children: detailData.district || "-" },
            {
              label: "Kelurahan/Desa",
              children: detailData.subdistrict || "-",
            },
          ],
    [detailData],
  );

  const areaResidenceItems = useMemo(
    () =>
      !detailData
        ? []
        : [
            { label: "Provinsi", children: detailData.domProvince || "-" },
            { label: "Kabupaten/Kota", children: detailData.domCity || "-" },
            { label: "Kecamatan", children: detailData.domDistrict || "-" },
            {
              label: "Kelurahan/Desa",
              children: detailData.domSubdistrict || "-",
            },
            { label: "Alamat", children: detailData.domAddress || "-" },
          ],
    [detailData],
  );

  /** 🔹 Data pelatihan */
  const trainingDataItems = useMemo(
    () =>
      !detailData
        ? []
        : [
            {
              label: "Negara Tujuan",
              children: detailData.requestedCountry || "-",
            },
            { label: "Sektor Pekerjaan", children: detailData.sectoral || "-" },
            { label: "Jabatan", span: 2, children: detailData.position || "-" },
          ],
    [detailData],
  );

  const standardRequirementDocs = [
    {
      key: "ktp",
      title: "KTP",
      required: true,
      description: null,
      formats: "jpeg, png, jpg, pdf",
      maxSize: "2.5 MB",
      match: (name) => /ktp/i.test(name),
    },
    {
      key: "kk",
      title: "Kartu Keluarga (KK)",
      required: true,
      description: null,
      formats: "jpeg, png, jpg, pdf",
      maxSize: "2.5 MB",
      match: (name) => /kartu keluarga|\bkk\b/i.test(name),
    },
    {
      key: "Ijazah Pendidikan",
      title: "Ijazah Pendidikan",
      required: true,
      description: "Unggah Ijazah pendidikan terakhir.",
      formats: "pdf",
      maxSize: "2.5 MB",
      match: (name) => /Dok Pendidikan/i.test(name),
    },
    {
      key: "keterampilan",
      title: "Dokumen Keterampilan",
      required: false,
      description:
        "Sertifikat atau dokumen keterampilan yang dimiliki (jika ada).",
      formats: "pdf",
      maxSize: "2.5 MB",
      match: (name) => /keterampilan/i.test(name),
    },
    {
      key: "bpjs",
      title: "Dokumen BPJS Kesehatan",
      required: false,
      description: null,
      formats: "jpeg, png, jpg, pdf",
      maxSize: "2.5 MB",
      match: (name) => /bpjs/i.test(name),
    },
    {
      key: "sehat",
      title: "Surat Keterangan Sehat Jasmani",
      required: false,
      description: null,
      formats: "pdf",
      maxSize: "2.5 MB",
      match: (name) => /sehat|jasmani/i.test(name),
    },
    {
      key: "pernyataan",
      title: "Surat Pernyataan",
      required: true,
      description: "Surat Pernyataan Bersedia Mengikuti Pelatihan.",
      formats: "pdf",
      maxSize: "2.5 MB",
      match: (name) => /pernyataan/i.test(name),
    },
    {
      key: "izin",
      title: "Surat Izin Pelatihan",
      required: false,
      description: "Surat Izin Mengikuti Pelatihan (jika ada).",
      formats: "pdf",
      maxSize: "2.5 MB",
      match: (name) => /izin/i.test(name),
    },
  ];

  const requirementDocuments = useMemo(() => {
    const rawFiles =
      detailData?.files || detailData?.dokumen_persyaratan_reguler || [];

    const matchedFiles = new Set();

    const standardDocsWithFiles = standardRequirementDocs.map((docDef) => {
      const file = rawFiles.find((f, idx) => {
        console.log(f, idx);

        if (matchedFiles.has(idx)) return false;
        const typeStr = f?.documentType || f?.label || f?.name || "";
        const matches = docDef.match(typeStr);
        if (matches) {
          matchedFiles.add(idx);
          return true;
        }
        return false;
      });

      return {
        ...docDef,
        file: file || null,
        fileName: file?.fileName || file?.file || file?.name || null,
        filePath: file?.filePath || file?.path || file?.url || null,
      };
    });

    const extraDocs = rawFiles
      .filter((_, idx) => !matchedFiles.has(idx))
      .map((f, idx) => ({
        key: `extra-${idx}`,
        title: f?.documentType || f?.label || `Dokumen Tambahan ${idx + 1}`,
        required: false,
        description: null,
        formats: "pdf, jpg, png",
        maxSize: "2.5 MB",
        file: f,
        fileName: f?.fileName || f?.file || f?.name || null,
        filePath: f?.filePath || f?.path || f?.url || null,
      }));

    return [...standardDocsWithFiles, ...extraDocs];
  }, [detailData]);

  if (!detailData)
    return (
      <Card title={"Detail CPMI"}>
        <Alert
          message="Data Tidak Ditemukan"
          description="Katalog pelatihan yang Anda cari tidak ditemukan."
          type="warning"
          showIcon
          action={<Button onClick={() => navigate(backRoute)}>Kembali</Button>}
        />
      </Card>
    );

  /** 🔹 Render utama */
  return (
    <>
      {contextHolder}
      <Card title={"Detail CPMI"}>
        <Descriptions
          style={{ marginBottom: 20 }}
          column={breakpoint.lg ? 2 : 1}
          bordered
          size="small"
          styles={{
            label: labelStyle,
            content: contentStyle,
          }}
          title={descriptionTitle("Status")}
          layout={breakpoint.lg ? "horizontal" : "vertical"}
          items={statusItems}
        />

        <Descriptions
          style={{ marginBottom: 20 }}
          column={breakpoint.lg ? 2 : 1}
          bordered
          size="small"
          styles={{
            label: labelStyle,
            content: contentStyle,
          }}
          title={descriptionTitle("Data Diri")}
          layout={breakpoint.lg ? "horizontal" : "vertical"}
          items={profileDataItems}
        />

        <Descriptions
          style={{ marginBottom: 20 }}
          column={breakpoint.lg ? 2 : 1}
          bordered
          styles={{
            label: labelStyle,
            content: contentStyle,
          }}
          size="small"
          title="Daerah Asal"
          layout={breakpoint.lg ? "horizontal" : "vertical"}
          items={areaOriginItems}
        />

        <Descriptions
          style={{ marginBottom: 20 }}
          column={breakpoint.lg ? 2 : 1}
          bordered
          styles={{
            label: labelStyle,
            content: contentStyle,
          }}
          size="small"
          title="Daerah Domisili"
          layout={breakpoint.lg ? "horizontal" : "vertical"}
          items={areaResidenceItems}
        />

        <Descriptions
          style={{ marginBottom: 20 }}
          column={breakpoint.lg ? 2 : 1}
          bordered
          size="small"
          styles={{
            label: labelStyle,
            content: contentStyle,
          }}
          title={descriptionTitle("Data Peminatan")}
          layout={breakpoint.lg ? "horizontal" : "vertical"}
          items={trainingDataItems}
        />

        <div style={{ marginBottom: 20 }}>
          <div style={{ marginBottom: 16 }}>
            {descriptionTitle("Dokumen Persyaratan Reguler")}
          </div>

          <Row gutter={[24, 20]}>
            {requirementDocuments.map((doc) => {
              const hasFile = Boolean(doc.fileName && doc.filePath);

              return (
                <Col xs={24} md={12} key={doc.key}>
                  <Flex
                    vertical
                    style={{ height: "100%", justifyContent: "space-between" }}
                  >
                    <div>
                      {/* Label with required asterisk */}
                      <Flex
                        align="center"
                        gap={4}
                        style={{ marginBottom: doc.description ? 2 : 6 }}
                      >
                        <Typography.Text
                          strong
                          style={{ fontSize: 14, color: "#1f2937" }}
                        >
                          {doc.title}
                        </Typography.Text>
                        {doc.required && (
                          <Typography.Text
                            style={{
                              color: "#ff4d4f",
                              fontWeight: "bold",
                              fontSize: 14,
                            }}
                          >
                            *
                          </Typography.Text>
                        )}
                      </Flex>

                      {/* Description / Subtitle */}
                      {doc.description && (
                        <Typography.Text
                          italic
                          type="secondary"
                          style={{
                            fontSize: 12,
                            marginBottom: 6,
                            display: "block",
                            color: "#6b7280",
                          }}
                        >
                          {doc.description}
                        </Typography.Text>
                      )}

                      {/* Upload / Document Box */}
                      <div
                        style={{
                          border: "1px solid #d9d9d9",
                          borderRadius: 8,
                          backgroundColor: "#fafafa",
                          padding: "6px 12px",
                          minHeight: 44,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 12,
                        }}
                      >
                        {hasFile ? (
                          <>
                            <Flex
                              align="center"
                              gap={8}
                              style={{ overflow: "hidden", flex: 1 }}
                            >
                              <Icon
                                icon="solar:document-text-linear"
                                width={18}
                                height={18}
                                style={{
                                  color: pallete.primary[500],
                                  flexShrink: 0,
                                }}
                              />
                              <Typography.Text
                                ellipsis
                                style={{
                                  fontSize: 13,
                                  fontWeight: 500,
                                  color: "#1f2937",
                                }}
                                title={doc.fileName}
                              >
                                {doc.fileName}
                              </Typography.Text>
                            </Flex>

                            <Flex
                              gap={8}
                              align="center"
                              style={{ flexShrink: 0 }}
                            >
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={() =>
                                  handleDownloadFile(doc.filePath, doc.fileName)
                                }
                              >
                                Unduh
                              </Button>
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={() => handlePreview(doc.filePath)}
                              >
                                Lihat
                              </Button>
                            </Flex>
                          </>
                        ) : (
                          <>
                            <Typography.Text
                              style={{
                                color: "#9ca3af",
                                fontSize: 13,
                              }}
                            >
                              Belum ada Dokumen yang diunggah
                            </Typography.Text>

                            <Button
                              size="small"
                              style={{
                                backgroundColor: "#ffffff",
                                borderColor: "#e5e7eb",
                                color: "#6b7280",
                                fontSize: 13,
                                height: 30,
                                cursor: "default",
                              }}
                            >
                              Pilih File
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Helper note */}
                    <Typography.Text
                      type="secondary"
                      style={{
                        fontSize: 11,
                        color: "#9ca3af",
                        marginTop: 4,
                        display: "block",
                      }}
                    >
                      Dokumen Dalam Bentuk: {doc.formats} | Ukuran Dokumen
                      Maksimal: {doc.maxSize}
                    </Typography.Text>
                  </Flex>
                </Col>
              );
            })}
          </Row>
        </div>

        {!showActions && (
          <Flex justify="flex-end" gap={12} style={{ marginTop: 24 }}>
            <Button
              type="primary"
              onClick={() => setConfirmVerify(true)}
              loading={isChangingStatus}
            >
              Verifikasi
            </Button>

            {/* <Button
              onClick={() => setConfirmRevise(true)}
              style={{ backgroundColor: pallete.indigo.main, color: "white" }}
            >
              Revisi
            </Button> */}

            <Button danger onClick={() => setConfirmReject(true)}>
              Tolak
            </Button>
          </Flex>
        )}
      </Card>

      {/* 🔹 Preview modal */}
      <DocumentPreviewModal
        open={!!previewFileData}
        src={previewFileData?.url || ""}
        isPdf={previewFileData?.isPdf}
        title={previewFileData?.isPdf ? "Preview Dokumen" : "Preview Gambar Pelatihan"}
        onClose={() => {
          if (previewFileData?.url) URL.revokeObjectURL(previewFileData.url);
          setPreviewFileData(null);
        }}
      />

      {/* 🔹 Modal Verifikasi */}
      {!showActions && (
        <>
          <Modal
            open={confirmVerify}
            centered
            footer={null}
            closable={false}
            width={400}
            onCancel={() => setConfirmVerify(false)}
          >
            <Flex vertical align="center" gap={12}>
              <div
                style={{
                  background: "#FFF4E5",
                  borderRadius: "50%",
                  padding: 16,
                }}
              >
                <ExclamationCircleFilled
                  style={{ color: "#FAAD14", fontSize: 32 }}
                />
              </div>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {MODAL_MESSAGES.VERIFY.TITLE}
              </Typography.Title>
              <Typography.Text style={{ textAlign: "center" }}>
                {MODAL_MESSAGES.VERIFY.DESCRIPTION}
              </Typography.Text>
              <Flex gap={8} style={{ marginTop: 16 }}>
                <Button
                  onClick={() => !isChangingStatus && setConfirmVerify(false)}
                >
                  Batal
                </Button>
                <Button
                  type="primary"
                  style={{ backgroundColor: pallete.primary[500] }}
                  loading={isChangingStatus}
                  disabled={isChangingStatus}
                  onClick={handleVerify}
                >
                  Verifikasi
                </Button>
              </Flex>
            </Flex>
          </Modal>

          <Modal
            open={confirmRevise}
            centered
            footer={null}
            closable={false}
            width={450}
            onCancel={() => !isChangingStatus && setConfirmRevise(false)}
          >
            <Flex vertical align="center" justify="center" gap={12}>
              <div
                style={{
                  backgroundColor: "#EEF2FF",
                  borderRadius: "50%",
                  padding: 16,
                }}
              >
                <ExclamationCircleFilled
                  style={{ color: "#4F46E5", fontSize: 32 }}
                />
              </div>

              <Typography.Title level={4} style={{ margin: 0 }}>
                Konfirmasi Revisi
              </Typography.Title>

              <Typography.Text style={{ textAlign: "center" }}>
                Masukkan alasan revisi untuk peserta ini.
              </Typography.Text>

              <div style={{ width: "100%", marginTop: 12 }}>
                <Typography.Text strong>Alasan Revisi</Typography.Text>
                <Input.TextArea
                  rows={4}
                  value={reviseReason}
                  onChange={(e) => setReviseReason(e.target.value)}
                  placeholder="Tuliskan alasan revisi..."
                  disabled={isChangingStatus}
                  maxLength={500}
                  showCount
                />
              </div>

              <Flex gap={8} style={{ marginTop: 16 }}>
                <Button
                  onClick={() => {
                    setConfirmRevise(false);
                    setReviseReason("");
                  }}
                  disabled={isChangingStatus}
                >
                  Batal
                </Button>

                <Button
                  type="primary"
                  onClick={handleRevise}
                  loading={isChangingStatus}
                >
                  Kirim Revisi
                </Button>
              </Flex>
            </Flex>
          </Modal>

          {/* 🔹 Modal Tolak */}
          <Modal
            open={confirmReject}
            centered
            footer={null}
            closable={false}
            width={450}
            onCancel={() => !isChangingStatus && setConfirmReject(false)}
          >
            <Flex vertical align="center" gap={12}>
              <div
                style={{
                  background: "#FFF1F0",
                  borderRadius: "50%",
                  padding: 16,
                }}
              >
                <CloseCircleFilled style={{ color: "#FF4D4F", fontSize: 32 }} />
              </div>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {MODAL_MESSAGES.REJECT.TITLE}
              </Typography.Title>
              <Typography.Text style={{ textAlign: "center" }}>
                {MODAL_MESSAGES.REJECT.DESCRIPTION}
              </Typography.Text>

              <div style={{ width: "100%", marginTop: 12 }}>
                <Typography.Text strong>
                  {MODAL_MESSAGES.REJECT.REASON_LABEL}
                </Typography.Text>
                <Input.TextArea
                  rows={4}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder={MODAL_MESSAGES.REJECT.REASON_PLACEHOLDER}
                  disabled={isChangingStatus}
                  maxLength={500}
                  showCount
                />
              </div>

              <Flex gap={8} style={{ marginTop: 16 }}>
                <Button
                  onClick={() => {
                    setConfirmReject(false);
                    setRejectReason("");
                  }}
                  disabled={isChangingStatus}
                >
                  Batal
                </Button>
                <Button
                  danger
                  type="primary"
                  onClick={handleReject}
                  loading={isChangingStatus}
                >
                  Kirim
                </Button>
              </Flex>
            </Flex>
          </Modal>
        </>
      )}
    </>
  );
}
